import React from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Polished destructive-action confirmation dialog.
 *
 * Used in place of `window.confirm(...)` so the UX matches Postmoro's dark
 * SaaS aesthetic and supports rich messaging (e.g. an extra warning row when
 * deleting a post that's already published to a real platform).
 *
 * Props:
 *   open, onOpenChange   — standard Radix Dialog controlled props
 *   title                — main heading
 *   description          — primary body copy
 *   secondaryNote        — optional second paragraph (e.g. "This will not
 *                          remove the post from the social platform.")
 *   confirmLabel         — defaults to "Delete"
 *   cancelLabel          — defaults to "Cancel"
 *   destructive          — bool (default true). When false, the confirm
 *                          button uses the primary brand color.
 *   loading              — disables buttons and shows a spinner on confirm
 *   onConfirm            — async handler. The dialog stays open until the
 *                          promise settles so callers can show a loader.
 *   testidPrefix         — for stable data-testids (default "confirm-dialog")
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  secondaryNote,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  destructive = true,
  loading = false,
  onConfirm,
  testidPrefix = "confirm-dialog",
}) {
  const confirmClass = destructive
    ? "bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90 focus-visible:ring-[color:var(--danger)] text-white border-transparent"
    : "bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] border-transparent";

  const handleConfirm = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    try {
      await onConfirm?.();
    } catch (_e) {
      // Caller is responsible for surfacing the error via toast. We only
      // need to ensure the dialog doesn't crash.
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] rounded-xl"
        data-testid={`${testidPrefix}-content`}
      >
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div
              className={
                "h-10 w-10 shrink-0 rounded-full flex items-center justify-center " +
                (destructive
                  ? "bg-[color:rgba(255,92,108,0.12)] text-[color:var(--danger)]"
                  : "bg-[color:var(--surface-2)] text-[color:var(--brand-primary)]")
              }
              aria-hidden="true"
            >
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <AlertDialogTitle
                className="text-[color:var(--text-1)] font-display text-lg leading-tight"
                data-testid={`${testidPrefix}-title`}
              >
                {title}
              </AlertDialogTitle>
              {description ? (
                <AlertDialogDescription
                  className="text-[color:var(--text-2)] mt-1.5 leading-relaxed"
                  data-testid={`${testidPrefix}-description`}
                >
                  {description}
                </AlertDialogDescription>
              ) : null}
            </div>
          </div>
        </AlertDialogHeader>
        {secondaryNote ? (
          <div
            className="mt-1 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2 text-xs text-[color:var(--text-2)] leading-relaxed"
            data-testid={`${testidPrefix}-secondary-note`}
          >
            {secondaryNote}
          </div>
        ) : null}
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            disabled={loading}
            className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)] hover:text-[color:var(--text-1)]"
            data-testid={`${testidPrefix}-cancel`}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={loading}
            onClick={handleConfirm}
            className={confirmClass + " font-semibold disabled:opacity-60 disabled:cursor-not-allowed"}
            data-testid={`${testidPrefix}-confirm`}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
